import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FlagIcon from "@mui/icons-material/Flag";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import FunctionsIcon from "@mui/icons-material/Functions";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { type FunctionComponent } from "react";
import Katex from "../latex/Katex";

interface NodeContextMenuAnchor {
  nodeId: string;
  nodeName: string;
  // Viewport coordinates of the right click
  top: number;
  left: number;
}

interface NodeContextMenuProps {
  anchor: NodeContextMenuAnchor | null;
  isDerivativeTarget: boolean;
  isCollapsed: boolean;
  onClose: () => void;
  onSetDerivativeTarget: (nodeId: string) => void;
  onClearDerivativeTarget: () => void;
  onExplainDerivative: (nodeId: string) => void;
  onToggleCollapse: (nodeId: string) => void;
  onDuplicate: (nodeId: string) => void;
  onDelete: (nodeId: string) => void;
}

const NodeContextMenu: FunctionComponent<NodeContextMenuProps> = ({
  anchor,
  isDerivativeTarget,
  isCollapsed,
  onClose,
  onSetDerivativeTarget,
  onClearDerivativeTarget,
  onExplainDerivative,
  onToggleCollapse,
  onDuplicate,
  onDelete,
}) => {
  // Runs the action and closes in one go, which is what every item wants
  const run = (action: () => void) => {
    return () => {
      action();
      onClose();
    };
  };

  return (
    <Menu
      open={anchor !== null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={
        anchor === null ? undefined : { top: anchor.top, left: anchor.left }
      }
      // Right clicking another node while the menu is open should feel
      // instant rather than animated
      transitionDuration={80}
      // While the menu is open its backdrop covers the canvas, so a second
      // right click lands there instead of on a node and nothing would stop
      // the browser from showing its own menu
      onContextMenu={(event) => {
        event.preventDefault();
      }}
      MenuListProps={{ dense: true, "aria-label": "node-context-menu" }}
      slotProps={{ paper: { sx: { minWidth: 232 } } }}
    >
      {anchor !== null && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", px: 2, pt: 0.5, pb: 1 }}
        >
          <Katex latex={anchor.nodeName} />
        </Typography>
      )}

      {isDerivativeTarget ? (
        <MenuItem onClick={run(onClearDerivativeTarget)}>
          <ListItemIcon>
            <FlagOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clear derivative target</ListItemText>
        </MenuItem>
      ) : (
        <MenuItem
          onClick={run(() => {
            if (anchor !== null) {
              onSetDerivativeTarget(anchor.nodeId);
            }
          })}
        >
          <ListItemIcon>
            <FlagIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Set as derivative target</ListItemText>
        </MenuItem>
      )}

      <MenuItem
        onClick={run(() => {
          if (anchor !== null) {
            onExplainDerivative(anchor.nodeId);
          }
        })}
      >
        <ListItemIcon>
          <FunctionsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Explain derivative</ListItemText>
      </MenuItem>

      <MenuItem
        onClick={run(() => {
          if (anchor !== null) {
            onToggleCollapse(anchor.nodeId);
          }
        })}
      >
        <ListItemIcon>
          {isCollapsed ? (
            <UnfoldMoreIcon fontSize="small" />
          ) : (
            <UnfoldLessIcon fontSize="small" />
          )}
        </ListItemIcon>
        <ListItemText>{isCollapsed ? "Expand" : "Collapse"}</ListItemText>
      </MenuItem>

      <MenuItem
        onClick={run(() => {
          if (anchor !== null) {
            onDuplicate(anchor.nodeId);
          }
        })}
      >
        <ListItemIcon>
          <ContentCopyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Duplicate</ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem
        onClick={run(() => {
          if (anchor !== null) {
            onDelete(anchor.nodeId);
          }
        })}
        sx={{ color: "error.main" }}
      >
        <ListItemIcon>
          <DeleteOutlineIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );
};

export default NodeContextMenu;
export type { NodeContextMenuAnchor };
